import type { Lang } from './landingCopy.js';
import type { DocKey, WarningCode } from './setupPlan.js';

/** Every string in the installation planner, in both languages. */
const en = {
  eyebrow: 'Installation planner',
  title: 'Plan your install in four questions',
  subtitle:
    'Answer what you know and this produces the commands to run, a starting configuration file, and the machine to ask for, sized from the measurements in the installation guide rather than guesswork.',
  step: (n: number, of: number) => `Step ${n} of ${of}`,
  back: 'Back',
  next: 'Next',
  startOver: 'Start over',

  q1: {
    title: 'Where will it run?',
    managed: { label: 'Managed cloud', hint: 'We run it. You add one script tag.' },
    self: { label: 'Your own server', hint: 'Reachable from the internet, because the SDK posts from your visitors’ browsers.' },
    private: { label: 'Private cloud', hint: 'Your VPC, subscription or OpenShift project, internal only.' },
    airgapped: { label: 'Air-gapped', hint: 'No outbound internet at all. Everything is carried in.' },
  },
  q2: {
    title: 'Which platform?',
    cloudTitle: 'Cloud',
    aws: 'AWS',
    azure: 'Azure',
    gcp: 'Google Cloud',
    vmware: 'VMware / OpenStack',
    other: 'Something else',
    runtimeTitle: 'How do you run containers?',
    compose: { label: 'Docker Compose', hint: 'One host. The simplest install, and what the guides are written against.' },
    kubernetes: { label: 'Kubernetes', hint: 'An existing cluster, or more than one node.' },
    openshift: { label: 'OpenShift', hint: 'Same chart, with the restricted-SCC and Route differences handled.' },
  },
  q3: {
    title: 'How do you want to provision it?',
    hint: 'Only the options that actually exist in the repository are offered.',
    terraform: { label: 'Terraform', hint: 'AWS. Creates the bucket, queue, instance role and host, with no static access keys anywhere.' },
    cloudformation: { label: 'CloudFormation', hint: 'AWS, one file. Secrets live in Secrets Manager, never in a stack event.' },
    helm: { label: 'Helm', hint: 'Kubernetes and OpenShift. Migration runs as a pre-upgrade hook.' },
    compose: { label: 'Docker Compose by hand', hint: 'You place the files and run two commands.' },
    tarball: { label: 'Carried tarball', hint: 'Built elsewhere, loaded from a file. Base images included.' },
  },
  q4: {
    title: 'How much traffic, and what do you keep?',
    sessions: 'Recorded sessions per month',
    sessionsHint: 'The number a customer can actually estimate. Everything else is derived from it.',
    retention: 'Retention (days)',
    retentionHint: 'This is storage cost and a privacy commitment at the same time.',
    pii: 'Enterprise PII masking',
    piiHint: 'Scrubs personal data before storage. It is CPU-bound, and the one feature that changes the machine.',
    managedDb: 'Managed database (RDS, Azure Database, Cloud SQL)',
    managedDbHint: 'Recommended for anything you cannot afford to restore by hand.',
  },

  result: {
    title: 'Your install',
    machine: 'Machine',
    machineOwn: 'Your own hardware',
    cpu: 'vCPU',
    ram: 'RAM',
    disk: 'System disk',
    objectStorage: 'Object storage for recordings',
    peak: 'Sized for peak concurrent visitors',
    replicas: 'Replicas',
    commands: 'Run this',
    config: 'Starting configuration',
    copy: 'Copy',
    copied: 'Copied',
    download: 'Download',
    watchOut: 'Worth knowing',
    readNext: 'Read next',
    managedTitle: 'Nothing to install',
    managedBody:
      'Add the snippet to your site and you are done. Your visitors’ browsers reach replayops.ai directly; your own servers need no outbound access at all.',
    perMonth: '/mo',
    perDay: '/day',
  },

  /**
   * Where each section lives, per language. The anchors are the slugs the docs
   * renderer produces from the headings, so the Turkish guide's are Turkish —
   * linking to the English ones from /tr would simply not scroll anywhere.
   */
  docLinks: {
    managed: { slug: 'installation', anchor: '1-managed-cloud', label: 'Managed cloud' },
    selfHost: { slug: 'installation', anchor: '2-public-facing-self-host', label: 'Public-facing self-host' },
    privateCloud: { slug: 'installation', anchor: '3-private-cloud', label: 'Private cloud' },
    airgapped: { slug: 'installation', anchor: '4-air-gapped-on-prem', label: 'Air-gapped (on-prem)' },
    sizing: { slug: 'installation', anchor: 'sizing-what-hardware-to-ask-for', label: 'Sizing: what hardware to ask for' },
    composeDeploy: { slug: 'deployment', anchor: 'docker-compose', label: 'Docker Compose' },
    kubernetes: { slug: 'deployment', anchor: 'kubernetes', label: 'Kubernetes' },
    openshift: { slug: 'deployment', anchor: 'openshift', label: 'OpenShift' },
    terraform: { slug: 'deployment', anchor: 'terraform', label: 'Terraform' },
    cloudformation: { slug: 'deployment', anchor: 'cloudformation', label: 'CloudFormation' },
  } as Record<DocKey, { slug: 'installation' | 'deployment'; anchor: string; label: string }>,

  warnings: {
    burstable:
      'Avoid a burstable instance (t3/B-series) at this volume. Under sustained ingest the CPU credits drain and throughput drops to about a third, with no error anywhere. It just looks like the application got slower.',
    bundledDatabase:
      'The bundled Postgres is a single container with no replication and no automated backups. Fine for a proof of concept; for a customer’s production data, point it at a managed database.',
    localStorageMultiNode:
      'At this volume put recordings in object storage. A ReadWriteOnce volume pins the api and worker pods to one node, which quietly caps everything above it.',
    airgappedNoAcme:
      'No Let’s Encrypt without the internet, so bring an internal CA certificate. Mount the CA bundle into the containers too if they must reach an SMTP relay or identity provider over TLS.',
    privateCloudPublicApp:
      'Session replay needs the visitor’s browser to reach the collector. If the recorded application is public, the collector must be reachable from wherever your users are. Publish just that endpoint and keep the dashboard private.',
    piiMemory:
      'The analyzer holds roughly 1 GB resident per worker, and more workers than one per 2 vCPU made it slower on every machine we measured.',
    retentionCost:
      'Retention is the whole storage bill at this volume. Confirm the number with whoever owns the privacy policy before sizing the bucket.',
  } as Record<WarningCode, string>,
};

const tr: typeof en = {
  eyebrow: 'Kurulum planlayıcı',
  title: 'Kurulumunuzu dört soruda planlayın',
  subtitle:
    'Bildiklerinizi yanıtlayın; karşılığında çalıştıracağınız komutları, başlangıç yapılandırma dosyasını ve isteyeceğiniz makineyi, tahminle değil kurulum kılavuzundaki ölçümlerle boyutlandırılmış olarak üretelim.',
  step: (n: number, of: number) => `Adım ${n} / ${of}`,
  back: 'Geri',
  next: 'İleri',
  startOver: 'Baştan başla',

  q1: {
    title: 'Nerede çalışacak?',
    managed: { label: 'Yönetilen bulut', hint: 'Biz çalıştırırız. Siz tek bir script etiketi eklersiniz.' },
    self: {
      label: 'Kendi sunucunuz',
      hint: 'İnternetten erişilebilir olmalı; çünkü SDK ziyaretçilerinizin tarayıcısından veri gönderir.',
    },
    private: { label: 'Özel bulut', hint: 'Kendi VPC’niz, aboneliğiniz ya da OpenShift projeniz, yalnızca içeriden erişilir.' },
    airgapped: { label: 'Tamamen kapalı', hint: 'Hiç dış internet yok. Her şey elle taşınır.' },
  },
  q2: {
    title: 'Hangi platform?',
    cloudTitle: 'Bulut',
    aws: 'AWS',
    azure: 'Azure',
    gcp: 'Google Cloud',
    vmware: 'VMware / OpenStack',
    other: 'Başka bir şey',
    runtimeTitle: 'Konteynerleri nasıl çalıştırıyorsunuz?',
    compose: {
      label: 'Docker Compose',
      hint: 'Tek sunucu. En basit kurulum ve kılavuzların yazıldığı biçim.',
    },
    kubernetes: { label: 'Kubernetes', hint: 'Mevcut bir küme ya da birden fazla düğüm.' },
    openshift: {
      label: 'OpenShift',
      hint: 'Aynı chart; kısıtlı SCC ve Route farkları hallediliyor.',
    },
  },
  q3: {
    title: 'Nasıl kurmak istiyorsunuz?',
    hint: 'Yalnızca depoda gerçekten var olan seçenekler gösterilir.',
    terraform: {
      label: 'Terraform',
      hint: 'AWS. Kovayı, kuyruğu, sunucu rolünü ve sunucuyu oluşturur. Hiçbir yerde sabit erişim anahtarı yok.',
    },
    cloudformation: {
      label: 'CloudFormation',
      hint: 'AWS, tek dosya. Sırlar Secrets Manager’da durur, yığın olaylarında hiç görünmez.',
    },
    helm: { label: 'Helm', hint: 'Kubernetes ve OpenShift. Migration bir pre-upgrade kancasıyla çalışır.' },
    compose: { label: 'Elle Docker Compose', hint: 'Dosyaları siz koyarsınız, iki komut çalıştırırsınız.' },
    tarball: { label: 'Taşınan arşiv', hint: 'Başka yerde derlenir, dosyadan yüklenir. Temel imajlar dâhil.' },
  },
  q4: {
    title: 'Ne kadar trafik, ne kadar saklama?',
    sessions: 'Aylık kaydedilen oturum',
    sessionsHint: 'Bir müşterinin gerçekten tahmin edebileceği sayı. Diğer her şey bundan türetilir.',
    retention: 'Saklama (gün)',
    retentionHint: 'Bu hem depolama maliyeti hem de bir gizlilik taahhüdüdür.',
    pii: 'Enterprise PII maskeleme',
    piiHint:
      'Kişisel veriyi depolamadan önce temizler. CPU’ya bağlıdır ve makineyi değiştiren tek özellik budur.',
    managedDb: 'Yönetilen veritabanı (RDS, Azure Database, Cloud SQL)',
    managedDbHint: 'Elle geri yüklemeyi göze alamayacağınız her şey için önerilir.',
  },

  result: {
    title: 'Kurulumunuz',
    machine: 'Makine',
    machineOwn: 'Kendi donanımınız',
    cpu: 'vCPU',
    ram: 'RAM',
    disk: 'Sistem diski',
    objectStorage: 'Kayıtlar için nesne depolama',
    peak: 'Şu eşzamanlı ziyaretçi tepesi için boyutlandırıldı',
    replicas: 'Kopya sayısı',
    commands: 'Şunu çalıştırın',
    config: 'Başlangıç yapılandırması',
    copy: 'Kopyala',
    copied: 'Kopyalandı',
    download: 'İndir',
    watchOut: 'Bilmekte fayda var',
    readNext: 'Devamını okuyun',
    managedTitle: 'Kurulacak bir şey yok',
    managedBody:
      'Snippet’i sitenize ekleyin, bu kadar. Ziyaretçilerinizin tarayıcıları doğrudan replayops.ai’ye bağlanır; kendi sunucularınızın hiçbir dış erişime ihtiyacı olmaz.',
    perMonth: '/ay',
    perDay: '/gün',
  },

  docLinks: {
    managed: { slug: 'installation', anchor: '1-yönetilen-bulut', label: 'Yönetilen bulut' },
    selfHost: { slug: 'installation', anchor: '2-dışarıya-açık-kendi-sunucunuz', label: 'Dışarıya açık kendi sunucunuz' },
    privateCloud: { slug: 'installation', anchor: '3-özel-bulut', label: 'Özel bulut' },
    airgapped: { slug: 'installation', anchor: '4-tamamen-kapalı-on-prem', label: 'Tamamen kapalı (on-prem)' },
    sizing: { slug: 'installation', anchor: 'boyutlandırma-müşteriden-ne-donanım-istemeli', label: 'Boyutlandırma: ne donanım istemeli' },
    composeDeploy: { slug: 'deployment', anchor: 'docker-compose', label: 'Docker Compose' },
    kubernetes: { slug: 'deployment', anchor: 'kubernetes', label: 'Kubernetes' },
    openshift: { slug: 'deployment', anchor: 'openshift', label: 'OpenShift' },
    terraform: { slug: 'deployment', anchor: 'terraform', label: 'Terraform' },
    cloudformation: { slug: 'deployment', anchor: 'cloudformation', label: 'CloudFormation' },
  },

  warnings: {
    burstable:
      'Bu hacimde patlamalı (t3/B serisi) makineden kaçının. Sürekli veri alımı altında CPU kredileri tükenir ve verim yaklaşık üçte bire düşer; hiçbir yerde hata görünmez, sadece uygulama yavaşlamış gibi olur.',
    bundledDatabase:
      'Pakete dahil Postgres, çoğaltması ve otomatik yedeği olmayan tek bir konteynerdir. Kavram kanıtı için uygundur; müşterinin üretim verisi için yönetilen bir veritabanı kullanın.',
    localStorageMultiNode:
      'Bu hacimde kayıtları nesne depolamaya koyun. ReadWriteOnce bir birim api ve worker podlarını tek bir düğüme sabitler ve üstündeki her şeyi sessizce sınırlar.',
    airgappedNoAcme:
      'İnternetsiz Let’s Encrypt olmaz, iç CA sertifikası getirin. Konteynerler bir SMTP aktarıcısına ya da kimlik sağlayıcıya TLS üzerinden ulaşacaksa CA paketini onlara da bağlayın.',
    privateCloudPublicApp:
      'Oturum kaydı, ziyaretçinin tarayıcısının collector’a ulaşmasını gerektirir. Kaydedilen uygulama halka açıksa collector, kullanıcılarınızın bulunduğu yerden erişilebilir olmalı. Yalnızca o uç noktayı yayımlayın, paneli özel tutun.',
    piiMemory:
      'Çözümleyici işçi başına yaklaşık 1 GB bellek tutar ve 2 vCPU başına bir işçiden fazlası, ölçtüğümüz her makinede sistemi yavaşlattı.',
    retentionCost:
      'Bu hacimde depolama faturasının tamamı saklama süresidir. Kovayı boyutlandırmadan önce bu sayıyı gizlilik politikasının sahibiyle doğrulayın.',
  },
};

export const SETUP_COPY: Record<Lang, typeof en> = { en, tr };
export type SetupCopy = typeof en;
